// public/js/modules/map/info-panel.js
// Floating asset info panel for the map.
//
// Unlike a MapLibre popup (which is anchored to a marker and moves with the
// map), this panel is docked to the top-left corner of the map viewport. It
// stays put while the history loads and the camera zooms out to fit the route,
// so it never drifts off-screen ("no matter where you are").
//
// It is dismissed (together with the route + waypoints) when the user clicks an
// empty part of the map. Clicking the route/waypoints or another asset does not
// dismiss it.

import { clearRoute, appendHistoryWindow, fitRouteToView } from './history.js';
import { openRangePickerModal } from '../ui/range-picker-modal.js';
import { closeCurrentPopup } from './state.js';
import { openSensorSidebar, closeSensorSidebar } from './sensor-chart.js';
import { formatLocalTime, formatTimeAgo } from '../../utils/asset-utils.js';
import { spinnerSVG } from '../ui/spinner.js';
import { slotForDevice, slotDisplayLabel } from '../../features/assets/slot-utils.js';
import { convertValue } from '../../utils/units.js';
import { tName } from '../demo-mode.js';
import { can, getCurrentUser } from '../rbac.js';
import { getTemplate, renderTemplate, SURFACES } from '../../features/display/template-store.js';

// Environmental/sensor fields shown on the info panel (from asset.lastSensors).
// [key, label, unit, decimals]
export const SENSOR_DISPLAY = [
  ['temperature', 'Temp', '°C', 1],
  ['probe1Temp', 'Probe 1', '°C', 1],
  ['probe2Temp', 'Probe 2', '°C', 1],
  ['humidity', 'Humidity', '%', 0],
  ['pressure', 'Pressure', 'hPa', 1],
  ['fuelLevel', 'Fuel', '%', 0],
  ['battery', 'Battery', 'V', 2],
  ['batteryPercent', 'Battery', '%', 0],
  ['rssi', 'RSSI', 'dBm', 0],
  ['rssiAvg', 'RSSI level', 'dBm', 1],
  ['rssiSpread', 'RSSI spread', 'dB', 1],
  ['rssiFloor', 'RSSI floor', 'dBm', 1],
  ['speed', 'Speed', 'km/h', 1],
  ['heading', 'Heading', '°', 0],
  ['distanceM', 'Distance', 'm', 2],
];

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Build the "Environment & sensors" section from the asset's stored last
// readings. Each badge is clickable and opens the sensor-history sidebar.
function buildSensorsHtml(asset) {
  const map = asset && asset.lastSensors;
  if (!map || typeof map !== 'object') return '';

  const assigned = Array.isArray(asset.assignedDeviceIds) ? new Set(asset.assignedDeviceIds) : null;

  const blocks = Object.values(map).map(d => {
    if (assigned && d.deviceId && !assigned.has(d.deviceId)) return '';
    const badges = SENSOR_DISPLAY.map(([k, label, unit, dec]) => {
      const v = d[k];
      if (v == null || v === '') return '';
      const num = Number(v);
      const { value: val, unit: dispUnit } = Number.isNaN(num)
        ? { value: v, unit }
        : convertValue(num, unit, dec);
      return `<span class="badge bg-light text-dark border me-1 mb-1 js-sensor-badge" role="button" tabindex="0"
                title="Show history chart" style="font-weight:500;cursor:pointer;">${label}: ${val} ${dispUnit}</span>`;
    }).filter(Boolean).join('');
    if (!badges) return '';
    // Prefer the slot's display label ("Engine Bag") so identical sensors stay
    // distinguishable and a renamed sub-asset shows its name, not the subType.
    const slot = slotForDevice(asset, d.deviceId);
    const name = slot
      ? slotDisplayLabel(slot, d)
      : (d.subType || (d.type === 'digitalmatter' ? 'GPS' : (d.type || d.deviceId || 'device')));
    return `<div class="mt-1">${badges}<div class="text-muted" style="font-size:0.7rem;">${escapeHtml(name)}${d.at ? ` · ${formatLocalTime(d.at)}` : ''}</div></div>`;
  }).filter(Boolean).join('');

  return blocks
    ? `<hr class="my-2"><div class="small"><strong>Environment &amp; sensors</strong>
         <div class="text-muted" style="font-size:0.68rem;">Click a value to view its history chart</div>
         ${blocks}</div>`
    : '';
}

// ── Card blocks (docs/asset-display-templates.md) ───────────────────────────
// The card is assembled from blocks whose ORDER and VISIBILITY come from the
// tenant's (or the user's) map-card template. Everything still renders exactly
// as before when no template is configured — the default template mirrors the
// previous hard-coded layout.
function mapCardBlocks() {
  try {
    const tpl = getTemplate(SURFACES.mapCard);
    const blocks = Array.isArray(tpl?.blocks) ? tpl.blocks : null;
    if (blocks?.length) return blocks.filter(b => b && b.visible !== false);
  } catch (e) { /* template store not ready → fall through to the default */ }
  return [{ type: 'title' }, { type: 'text' }, { type: 'position' }, { type: 'sensors' }, { type: 'buttons' }];
}

// Identity line: the full name + status/type/tenant badges (the 'title' block's
// body). The name wraps here, where the UUID used to sit: the header can only
// hold one line, and a cut-off name told the user less than nothing. The UUID
// moved to the copy button in the footer.
function buildIdentityHtml(asset) {
  if (!mapCardBlocks().some(b => b.type === 'title')) return '';
  const subtitle = cardSubtitle(asset);
  return `
      ${subtitle ? `<div class="map-info-panel-subtitle">${escapeHtml(subtitle)}</div>` : ''}
      <span class="badge bg-${asset.status === 'active' ? 'success' : 'warning'}">${escapeHtml(asset.status || 'active')}</span>
      <span class="badge bg-secondary ms-1">${escapeHtml(asset.type || 'other')}</span>
      ${asset.tenantName || asset.tenantId
        ? `<span class="badge bg-info ms-1">Tenant: ${tName(asset.tenantName || asset.tenantId)}</span>`
        : ''}
      <hr class="my-2">`;
}

// Free-text blocks with ${field} placeholders — the configurable heart of the
// card. Lines whose variables are all empty collapse (renderTemplate handles it).
function buildTextBlocksHtml(asset) {
  const out = [];
  for (const b of mapCardBlocks()) {
    if (b.type !== 'text') continue;
    const tplText = Array.isArray(b.lines) ? b.lines.join('\n') : String(b.lines || '');
    if (!tplText.trim()) continue;
    let html = '';
    try { html = renderTemplate(tplText, asset); } catch (e) { html = ''; }
    if (html) out.push(`<div class="small mb-2">${html}</div>`);
  }
  return out.join('');
}

function buildPositionHtml(asset, lat, lng) {
  if (!mapCardBlocks().some(b => b.type === 'position')) return '';
  // A device that reports without locating itself (GPS-less heartbeat) keeps
  // the asset's lastFixAt behind lastUpdated. Past a couple of hours that gap
  // is worth saying out loud: the pin is where the asset WAS, the device is alive.
  const stale = isPositionStale(asset);
  const inFlight = (asset.assignedDevices || []).some(d => d.inFlight === true);
  return `
      ${inFlight ? `<div class="small text-info mb-1"><i class="fa-solid fa-plane me-1"></i>In flight - the tracker has suspended uploads until landing</div>` : ''}
      <div class="small">
        <strong>${stale ? 'Last known position' : 'Last Position'}</strong><br>
        ${Number.isFinite(lat) ? lat.toFixed(5) : '—'}, ${Number.isFinite(lng) ? lng.toFixed(5) : '—'}
        ${Number.isFinite(lat) && Number.isFinite(lng) ? `
          <a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer"
             class="map-info-panel-maps-link" title="Open these coordinates in Google Maps">Google Maps <i class="fa-solid fa-arrow-up-right-from-square"></i></a>` : ''}<br>
        ${Number(asset.lastAccuracy) > 0 ? `<span class="text-muted">Accuracy: ±${Math.round(Number(asset.lastAccuracy))} m</span><br>` : ''}
        ${stale
          ? `<span class="text-warning"><i class="fa-solid fa-triangle-exclamation me-1"></i>Position fix: ${formatLocalTime(asset.lastFixAt)} (${formatTimeAgo(asset.lastFixAt)})</span><br>
             <span class="text-muted">Device last seen: ${formatTimeAgo(asset.lastUpdated)} - it has not located itself since</span>`
          : `<span class="text-muted">Last updated: ${formatLocalTime(asset.lastUpdated)}</span>`}
      </div>
      ${asset.lastUpdated && !stale ? `
      <div class="mt-1 small text-info">
        <i class="fa-solid fa-clock"></i>
        ${formatTimeAgo(asset.lastUpdated)}
      </div>` : ''}`;
}

// "Stale" = the device has reported (lastUpdated) for more than two hours
// without a real fix (lastFixAt). Assets written before lastFixAt existed have
// no lastFixAt and are never flagged.
export function isPositionStale(asset) {
  if (!asset?.lastFixAt || !asset?.lastUpdated) return false;
  return new Date(asset.lastUpdated).getTime() - new Date(asset.lastFixAt).getTime() > 2 * 60 * 60 * 1000;
}

// Front photo on the card — off by default, enabled per tenant/user. The URL is
// filled in asynchronously so the card never waits on an image request.
function buildPhotoHtml(asset) {
  if (!mapCardBlocks().some(b => b.type === 'photo')) return '';
  return `<div class="mt-2 js-card-photo" data-asset="${escapeHtml(asset.assetUUID || '')}"></div>`;
}

async function fillCardPhoto(root, asset) {
  const host = root.querySelector('.js-card-photo');
  if (!host || !asset?.assetUUID) return;
  try {
    const { listAssetFiles, getFileUrl } = await import('../../features/files/api.js');
    const photos = await listAssetFiles(asset.assetUUID, { kind: 'photo' });
    const primary = photos.find(p => p.isPrimary) || photos[0];
    if (!primary) return;
    // getFileUrl returns the bare URL string.
    const url = await getFileUrl(primary.fileId, 'thumb');
    if (!url) return;
    host.innerHTML = `<img src="${escapeHtml(url)}" alt="" loading="lazy"
      style="width:100%;max-height:150px;object-fit:cover;border-radius:8px;">`;
  } catch (e) { /* a missing photo must never break the card */ }
}

// The card header text: the title block's own template when one is configured
// (Settings > Display > Map asset card), else the shortest thing that still
// identifies the asset. Short matters twice over - the header is a single line
// between four icon buttons, and a collapsed card shows nothing else.
function cardHeading(asset) {
  const block = mapCardBlocks().find(b => b.type === 'title');
  const tpl = typeof block?.template === 'string' ? block.template.trim() : '';
  if (tpl) {
    try {
      const text = renderTemplate(tpl, asset, { plain: true, maxLength: 120 });
      if (text) return text;
    } catch (e) { /* fall back to the reference */ }
  }
  return asset.reference || asset.serial || asset.label || asset.name || 'Unnamed Asset';
}

// The asset's name, wrapped under the header. Dropped when the header already
// says it (an asset without a reference, or a title template that renders the
// name itself), so the card never prints the same string twice.
function cardSubtitle(asset) {
  const name = asset.label || asset.name || '';
  return name && name !== cardHeading(asset) ? name : '';
}

function buildPanelHtml(asset) {
  const lng = parseFloat(asset.lastLon);
  const lat = parseFloat(asset.lastLat);
  const displayName = cardHeading(asset);
  const uuid = asset.assetUUID || '';
  const showUuid = uuid && mapCardBlocks().some(b => b.type === 'title');

  return `
    <div class="map-info-panel-header">
      <button type="button" class="map-info-panel-toggle js-panel-toggle" aria-label="Expand/collapse" title="Expand/collapse">
        <i class="fa-solid fa-chevron-up"></i>
      </button>
      <strong class="map-info-panel-title" title="${escapeHtml(displayName)}">${escapeHtml(displayName)}</strong>
      <button type="button" class="map-info-panel-toggle js-panel-goto-pos" aria-label="Zoom to current position" title="Zoom to current position">
        <i class="fa-solid fa-location-crosshairs"></i>
      </button>
      <button type="button" class="map-info-panel-toggle js-panel-fit-path" aria-label="Zoom to whole path" title="Zoom to whole path">
        <i class="fa-solid fa-route"></i>
      </button>
      <button type="button" class="map-info-panel-close js-panel-close" aria-label="Close" title="Close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
    <div class="map-info-panel-body">
      ${buildIdentityHtml(asset)}
      ${buildTextBlocksHtml(asset)}
      ${buildPositionHtml(asset, lat, lng)}
      ${buildPhotoHtml(asset)}
      ${buildSensorsHtml(asset)}
      <div class="map-info-panel-actions">
        <button class="btn btn-primary w-100 js-open-asset">
          <i class="fa-solid fa-up-right-from-square me-1"></i> Open asset page
        </button>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-outline-secondary flex-grow-1 js-load-history-window" style="min-width:0;"
                  title="Load an extra history window onto the path (adds to what's shown)">
            <i class="fa-regular fa-calendar-days me-1"></i> History
          </button>
          ${can(getCurrentUser().groups || [], 'manageAssetFiles') ? `
          <button class="btn btn-sm btn-outline-secondary flex-grow-1 js-add-photo" style="min-width:0;"
                  title="Take or attach a photo of this asset">
            <i class="fa-solid fa-camera me-1"></i> Photo
          </button>` : ''}
        </div>
      </div>
      <div class="map-info-panel-foot${showUuid ? ' has-content' : ''}">
        <div class="map-info-panel-status js-panel-status" hidden></div>
        ${showUuid ? `
        <button type="button" class="map-info-panel-uuid js-copy-uuid" data-uuid="${escapeHtml(uuid)}"
                title="Copy the full asset ID (${escapeHtml(uuid)})">
          ${escapeHtml(uuid.slice(0, 8))} <i class="fa-regular fa-copy"></i>
        </button>` : ''}
      </div>
    </div>`;
}

let panelEl = null;
let currentPanelAsset = null;

function ensurePanel(map) {
  if (panelEl) return panelEl;
  panelEl = document.createElement('div');
  panelEl.className = 'map-info-panel';
  map.getContainer().appendChild(panelEl);
  return panelEl;
}

export function getPanelAsset() {
  return currentPanelAsset;
}

export function showAssetInfoPanel(asset, map) {
  if (!asset || !map) return;

  // Any lingering route-point popup should go away when we open a fresh card.
  closeCurrentPopup();

  currentPanelAsset = asset;
  const el = ensurePanel(map);
  el.innerHTML = buildPanelHtml(asset);
  el.style.display = 'block';

  // On mobile the panel would cover most of the map, so start collapsed (header
  // only) and let the user expand it; on desktop it opens fully as before.
  const collapsed = window.innerWidth < 768;
  el.classList.toggle('collapsed', collapsed);
  const toggleBtn = el.querySelector('.js-panel-toggle');
  const syncToggleIcon = () => {
    const icon = toggleBtn?.querySelector('i');
    if (icon) icon.className = el.classList.contains('collapsed') ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up';
  };
  syncToggleIcon();
  toggleBtn?.addEventListener('click', () => {
    el.classList.toggle('collapsed');
    syncToggleIcon();
  });

  el.querySelector('.js-panel-close')?.addEventListener('click', () => {
    hideAssetInfoPanel();
    clearRoute(map);
  });

  // Camera-only helpers: neither touches the drawn path.
  el.querySelector('.js-panel-goto-pos')?.addEventListener('click', () => {
    const lng = parseFloat(asset.lastLon);
    const lat = parseFloat(asset.lastLat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      window.showToast?.('No current position for this asset', 'info');
      return;
    }
    map.flyTo({ center: [lng, lat], zoom: 15, duration: 700 });
  });
  el.querySelector('.js-panel-fit-path')?.addEventListener('click', () => {
    if (!fitRouteToView(map)) window.showToast?.('No path loaded yet', 'info');
  });

  el.querySelector('.js-open-asset')?.addEventListener('click', () => {
    // Go straight to this asset's detail page.
    hideAssetInfoPanel();
    window.location.hash = `#assets/${asset.assetUUID}`;
  });

  // Load an extra history window onto the path (centre date + duration slider).
  // Points ADD to what's already drawn (deduped), so several windows stack up.
  el.querySelector('.js-load-history-window')?.addEventListener('click', () => {
    openRangePickerModal({
      title: 'Load history window',
      hint: 'These points are ADDED to the path already shown; overlapping windows count each point once.',
      maxDays: 120,
      onApply: ({ fromMs, toMs }) => {
        setPanelHistoryStatus('loading');
        appendHistoryWindow(asset.assetUUID, map, new Date(fromMs).toISOString(), new Date(toMs).toISOString())
          .then(s => setPanelHistoryStatus(s === 'ok' ? 'ok' : s))
          .catch(() => setPanelHistoryStatus('error'));
      },
    });
  });

  // The asset ID is rarely read but often pasted (support tickets, the API), so
  // it is a copy button rather than a line of text taking up the card.
  el.querySelector('.js-copy-uuid')?.addEventListener('click', async (ev) => {
    const uuid = ev.currentTarget.dataset.uuid || '';
    if (!uuid) return;
    try {
      await navigator.clipboard.writeText(uuid);
      window.showToast?.('Asset ID copied', 'success');
    } catch (e) {
      window.showToast?.('Could not copy - the browser blocked clipboard access', 'warning');
    }
  });

  // Front photo (only when the template asks for it) — resolved after paint.
  fillCardPhoto(el, asset);

  // On-site photo capture straight from the map card — the phone use case
  // (docs/asset-files.md §9): stand at the asset, tap, shoot, done.
  el.querySelector('.js-add-photo')?.addEventListener('click', async () => {
    const { openUploader } = await import('../../features/files/uploader.js');
    openUploader({
      assetUUID: asset.assetUUID, kind: 'photo',
      onDone: (recs) => { if (recs?.length) window.showToast?.('Photo added to this asset', 'success'); },
    });
  });

  // Clicking any sensor value opens the (single, shared) history chart sidebar.
  el.querySelectorAll('.js-sensor-badge').forEach(badge => {
    const open = () => openSensorSidebar(asset, map);
    badge.addEventListener('click', open);
    badge.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); }
    });
  });

  attachMapBackgroundClick(map);
}

// Footer status line in the info card (small spinner + text) for the
// non-blocking history load triggered alongside the panel.
//   'loading' → spinner + "Loading history…"
//   'empty'   → no data in the last 14 days
//   'error'   → load failed
//   'ok'/null → hidden
export function setPanelHistoryStatus(state) {
  if (!panelEl || panelEl.style.display === 'none') return;
  const footer = panelEl.querySelector('.js-panel-status');
  if (!footer) return;

  if (state === 'loading') {
    footer.hidden = false;
    footer.innerHTML = `${spinnerSVG(22)}<span>Loading history…</span>`;
  } else if (state === 'empty') {
    footer.hidden = false;
    footer.innerHTML = `<i class="fa-solid fa-circle-info"></i><span>No history (last 14 days)</span>`;
  } else if (state === 'error') {
    footer.hidden = false;
    footer.innerHTML = `<i class="fa-solid fa-triangle-exclamation"></i><span>History unavailable</span>`;
  } else {
    footer.hidden = true;
    footer.innerHTML = '';
  }

  // The footer rule only earns its line when something sits on it.
  const foot = panelEl.querySelector('.map-info-panel-foot');
  if (foot) foot.classList.toggle('has-content', !footer.hidden || !!foot.querySelector('.js-copy-uuid'));
}

export function hideAssetInfoPanel() {
  if (panelEl) panelEl.style.display = 'none';
  currentPanelAsset = null;
  // Stop a later base-layer switch from re-drawing the dismissed route.
  window.currentViewedHistoryAssetId = null;
  closeSensorSidebar();
}

// Dismiss the panel + route when the user clicks empty map (not the route,
// waypoints, clusters or another asset point — those have their own handlers).
function attachMapBackgroundClick(map) {
  if (map.__mapBackgroundClick) return;
  map.__mapBackgroundClick = true;

  // Layers whose features represent "interactive" things; a click that hits one
  // of these must NOT dismiss the panel.
  const INTERACTIVE_LAYERS = [
    'history-route-hit-layer',
    'unclustered-points',
    'clusters',
    'cluster-count'
  ];

  map.on('click', (e) => {
    if (!currentPanelAsset) return;
    // Frozen record-mode trails have per-asset hit layers — treat them as
    // interactive too, so clicking one shows its point popup without
    // dismissing the panel.
    const frozenHitLayers = (map.getStyle()?.layers || [])
      .map(l => l.id)
      .filter(id => id.startsWith('frozen-trail-') && id.endsWith('-hit-layer'));
    const layers = [...INTERACTIVE_LAYERS, ...frozenHitLayers].filter(id => map.getLayer(id));
    const hits = layers.length ? map.queryRenderedFeatures(e.point, { layers }) : [];
    if (hits.length) return; // clicked the route / a waypoint / a cluster / an asset
    hideAssetInfoPanel();
    clearRoute(map);
  });
}
